// @flow
import React from 'react'
import TextWithNewlines from '../../TextWithNewlines'
import styled, { type StyledComponent } from 'styled-components'
import logo from '../images/instagram-logo.png'
import OptionalResponsibleImage from './OptionalResponsibleImage'
import type { Public, Crop, Image, PersistedImage } from '../../types'
import { translateFunc, translations } from '../../../shared/localize'
import _ from 'lodash'

import rightArrowIcon from '../images/instagram-arrow-right.png'
import moreIcon from '../images/instagram-more.png'
import heartIcon from '../images/instagram-heart.png'
import cloudIcon from '../images/instagram-cloud.png'
import planeIcon from '../images/instagram-plane.png'
import bookmarkIcon from '../images/instagram-bookmark.png'
import brandIcon from '../images/instagram-brand.png'

const MONOSPACE_LETTER_SPACING = 0.4

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  font-size: 14px;
  background-color: #ffffff;
  border: 1px solid #b3b3b3;
`
const Heading: StyledComponent<Object, any, any> = styled.div`
 &:after {
  content: '';
  display: table;
  clear: both;
`
const HeadingLogo: StyledComponent<Object, any, any> = styled.div`
  position: relative;
  height: 40px;
  background-color: #f9fafa;
  border-bottom: 1px solid #dcdcdc;

  &:before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 68px;
    height: 20px;
    margin: -10px 0 0 -34px;
    background: url(${brandIcon}) no-repeat;
    background-size: cover;
  }
`
const HeadingContainer: StyledComponent<Object, any, any> = styled.div`
  position: relative;
  overflow: hidden;
  padding: 8px 0 10px 10px;
  font-size: 0;
`
const MoreIcon: StyledComponent<Object, any, any> = styled.div`
  position: absolute
  top: 20px;
  right: 4px;
  width: 20px;
  height: 6px;
  background: url(${moreIcon}) no-repeat
  background-size: 70% 70%;
`
const HeadingImage: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: 24px;
  height: 24px;
  margin: 0 10px 0 0;
  background: url(${({ url }: Object) => url || logo}) no-repeat;
  ${({ url }: Object) => url && 'border-radius: 50%'};
  background-size: cover;
`
const HeadingTitle: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  vertical-align: middle;
  width: 100%;
  font-size: 12px;
  line-height: 1;

  &:first-of-type {
    letter-spacing: ${MONOSPACE_LETTER_SPACING}px;
    font-weight: 500;
  }

  &:last-of-type {
    font-size: 11px;
  }
`
const Container: StyledComponent<Object, any, any> = styled.div`
  padding: 0 10px;
`
const ContainerCanvas: StyledComponent<Object, any, any> = styled.div`
  margin: 0 -10px;
  text-align: center;
`
const ContainerMore: StyledComponent<Object, any, any> = styled.div`
  position: relative;
  padding: 10px 10px 10px 0;
  color: #3397f0;
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    width: 8px;
    height: 13px;
    margin-top: -8px;
    background: url(${rightArrowIcon}) no-repeat center center;
    background-size: 100% 100%;
  }
`
const ContainerToolbar: StyledComponent<Object, any, any> = styled.div`
  position: relative;
  padding: 9px 0 8px;
  border-top: 1px solid #e6e6e6;
  font-size: 0;
`
const ContainerText: StyledComponent<Object, any, any> = styled.div`
  min-height: 80px;
  padding: 11px 0;
  color: #2f2f2f;
  font-size: 12px;
  width: 320px;
  letter-spacing: ${MONOSPACE_LETTER_SPACING}px;
`
const Icon: StyledComponent<Object, any, any> = styled.span`
  display: inline-block;
  vertical-align: middle;
`
const IconLike: StyledComponent<Object, any, any> = styled(Icon)`
  width: 21px;
  height: 18px;
  background: url(${heartIcon}) no-repeat center center;
  background-size: cover;
`
const IconMessage: StyledComponent<Object, any, any> = styled(Icon)`
  width: 21px;
  height: 20px;
  background: url(${cloudIcon}) no-repeat center center;
  background-size: cover;
`
const IconBack: StyledComponent<Object, any, any> = styled(Icon)`
  margin-top: 2px;
  width: 21px;
  height: 18px;
  background: url(${planeIcon}) no-repeat center center;
  background-size: cover;
`
const IconSpacer: StyledComponent<Object, any, any> = styled.div`
  display: inline-block;
  width: 16px;
`

const IconBookmark: StyledComponent<Object, any, any> = styled(Icon)`
  position: absolute;
  right: 0;
  width: 15px;
  height: 21px;
  background: url(${bookmarkIcon}) no-repeat center center;
  background-size: cover;
`

type Props = {|
  title: string,
  description: string,
  publicInfo: Public,
  imageCrop: ?Crop,
  image: ?Image | ?PersistedImage,
  linkButton: string
|}

const t = translateFunc(translations.provider_previews.facebook)

export default function InstagramPreview(props: Props) {
  const { title, description, publicInfo, image, imageCrop, linkButton } = props

  return (
    <Wrapper>
      <Heading>
        <HeadingLogo />
        <HeadingContainer>
          <div
            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
          >
            <HeadingImage url={publicInfo && publicInfo.image_url} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <HeadingTitle>
                {_.isEmpty(publicInfo) ? 'Название группы' : publicInfo.label}
              </HeadingTitle>
              <HeadingTitle>Реклама</HeadingTitle>
            </div>
          </div>
          <MoreIcon />
        </HeadingContainer>
      </Heading>
      <Container>
        <ContainerCanvas>
          <OptionalResponsibleImage image={image} imageCrop={imageCrop} width={340} ratio={1} />
        </ContainerCanvas>
        <ContainerMore>{t(linkButton)}</ContainerMore>
        <ContainerToolbar>
          <IconLike />
          <IconSpacer />
          <IconMessage />
          <IconSpacer />
          <IconBack />
          <IconBookmark />
        </ContainerToolbar>
        <ContainerText>
          {title}
          <div>
            <TextWithNewlines value={description} />
          </div>
        </ContainerText>
      </Container>
    </Wrapper>
  )
}
